"use client";

import { FormEvent, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { authClient } from "src/lib/auth-client";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Insira um e-mail válido.");
      return;
    }

    setIsLoading(true);
    try {
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/passwordrecovery`,
      });
      setSuccessMessage("Link enviado! Verifique sua caixa de entrada.");
      setEmail("");
    } catch {
      // A resposta permanece indistinguível para não revelar contas existentes.
      setSuccessMessage("Link enviado! Verifique sua caixa de entrada.");
      setEmail("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-10">
          <div className="relative w-7 h-7">
            <Image
              src="/logo.png"
              alt="Waldem"
              fill
              className="object-contain"
            />
          </div>
          <span className="font-bold text-lg tracking-tight text-neutral-900">
            Waldem
          </span>
        </div>

        {successMessage ? (
          <div className="flex flex-col items-center text-center gap-4 py-8">
            <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 mb-1">
                E-mail enviado
              </h2>
              <p className="text-sm text-neutral-500">{successMessage}</p>
            </div>
            <Link
              href="/"
              className="mt-2 text-sm text-neutral-500 hover:text-neutral-900 flex items-center gap-1.5 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o login
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 mb-1.5">
                Recuperar conta
              </h1>
              <p className="text-sm text-neutral-500">
                Informe seu e-mail e enviaremos um link para redefinir sua
                senha.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              noValidate
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="email"
                  className="text-xs font-medium text-neutral-700"
                >
                  E-mail
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="exemplo@empresa.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  required
                  className={`w-full px-3.5 py-2.5 rounded-xl text-sm border bg-white outline-none transition-all placeholder:text-neutral-300
                    ${
                      errorMessage
                        ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                        : "border-neutral-200 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100"
                    }`}
                />
                {errorMessage && (
                  <p className="text-xs text-red-500">{errorMessage}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-neutral-900 text-white text-sm font-medium
                  hover:bg-neutral-800 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150
                  shadow-lg shadow-neutral-900/20 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Enviar link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <Link
              href="/"
              className="mt-6 flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
